import { lang } from '@umu/ukit-lang';
/**
 * 文件选择，外部可通过传递 className 及 children 自定义
 *
 * 所有环境统一采用：<label htmlFor> 包裹 children，input 移出视区；
 * label 的 onClick 中主动 input.click()，保证在鸿蒙/微信等 WebView 下也能稳定调起选择框。
 * 鸿蒙仅影响：multiple 强制为 false、accept 不设置（格式由 validateExt 等校验）。
 */
import React, { useId, useRef } from 'react';

import umuWebview from '@umu/umu-webview';
import { classSet } from '@umu/umu-dom';
import { Toast } from 'common/src/services';
import uniqueId from 'lodash/uniqueId';

export type IFile = File & { uniqueId: string };

import { getUploadResult, expandAccept, expandMediaAccept, IEncodeResult } from '../../services/uploader/uploader';
import { EMediaType, IUploadProgress } from '@umu/umu-types';

import './fileSelect.scss';
import { isHarmonyOS } from '@umu/umu-ua';

export interface IEndData {
    previewUrl: string;
    url: string;
    fileName: string;
    resourceId: string;
}

export type IFileSelectProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
    /** 业务资源类型 */
    mediaType: EMediaType | EMediaType[];
    /** 登录用户身份 */
    teacherId: string;
    onlyPreview?: boolean;
    /** 处理完成一条数据前的回调，入参为资源 url */
    onPreview?: (objectUrl: string, file: IFile) => void;
    /** 处理完成一条数据后的回调，入参为资源 url */
    onData?: (previewUrl: string, url: string, fileName: string, resourceId: any, file?: IFile) => void;
    /** 开始处理文件 */
    onStart?: (files: IFile[]) => void;
    onSelectReady?: (files: Array<{ file: IFile; previewUrl: string }>) => void;
    /** 文件全部上传完成 */
    onEnd?: (datas: IEndData[]) => void;
    /** 文件选择完成本地检测 */
    onCheck?: (files: IFile[]) => boolean | Promise<boolean>;
    /** 发送错误时 */
    onUploadError?: (errorMessage: Error, files: IFile[]) => void;
    progressHandler?: (fileName: string, progress: IUploadProgress, uniqueId?: string) => void;
    multiple?: boolean;
    accept?: string;
    isValidate?: boolean; // 由于浏览器不兼容accept传参方式 添加该参数，更多层校验通过下面validate 方法进行校验
    validate?(files: FileList): boolean;
    supportsResumeUpload?: boolean;
};

interface IPreviewResult {
    file: IFile;
    previewUrl: string;
}

export const IMG_MAX_SIZE = 10 * 1024 * 1024;

export function FileSelect({
    multiple = false,
    mediaType,
    teacherId,
    onlyPreview,
    onPreview,
    onData,
    children,
    className,
    disabled,
    onStart,
    onCheck,
    onEnd,
    onSelectReady,
    onUploadError,
    progressHandler,
    accept,
    isValidate,
    validate,
    supportsResumeUpload = false,
    onClick: propsOnClick,
    ...props
}: IFileSelectProps) {
    const onProgress = progressHandler;
    const input = useRef<HTMLInputElement>(null);
    const inputId = useId();
    const ua = umuWebview.UA_INFO;
    const inputAccept = typeof accept === 'string' ? expandAccept(accept) : expandMediaAccept(mediaType);

    function preview(file: IFile) {
        const previewUrl = URL.createObjectURL(file);

        if (onPreview) {
            onPreview(previewUrl, file);
        }
        return {
            file,
            previewUrl,
        };
    }

    async function encode({ file, previewUrl }: IPreviewResult): Promise<IEncodeResult> {
        const result: Blob = file;
        const fileExt = (file.name.split(/\./g).pop() || '').toLocaleLowerCase();

        const fileType = file.type;
        let fileMediaType = Array.isArray(mediaType) ? mediaType[0] : mediaType;
        if (Array.isArray(mediaType)) {
            if (/^(video|audio)/.test(fileType)) {
                fileMediaType = EMediaType.VIDEO_WEIKE;
            } else if (/^(application|text)/.test(fileType)) {
                fileMediaType = EMediaType.DOC_WEIKE;
            } else if (fileType.startsWith('image/')) {
                fileMediaType = EMediaType.PIC_WEIKE;
            }
        }

        return Promise.resolve({
            blob: result,
            ext: fileExt,
            previewUrl,
            uniqueId: file.uniqueId,
            fileName: file.name,
            mediaType: fileMediaType,
            teacherId,
            onProgress: (fileName: string, progress: IUploadProgress) => {
                if (onProgress instanceof Function) {
                    onProgress(fileName, progress, file.uniqueId);
                }
            },
        });
    }

    /**
     * @desc 手动的校验，因为某些移动端浏览器不支持 accept
     * @param files FileList
     */
    const validateExt = (files: IFile[]) => {
        if (!inputAccept) {
            return true;
        }
        const acceptArr = inputAccept.split(',');

        let flag = true;
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const ext = (file.name.split(/\./g).pop() || '').toLocaleLowerCase();
            flag = acceptArr.indexOf(ext) !== -1;
            if (!flag) {
                break;
            }
        }
        return flag;
    };

    const extError = (files: IFile[]) => {
        Toast.show({
            message: lang('文件格式暂不支持，请重新选择'),
            callback() {
                if (onUploadError) {
                    onUploadError(new Error('IFile format is not legal'), files);
                }
            },
        });
    };

    async function processFiles(event: React.ChangeEvent<HTMLInputElement>) {
        const files = event.target.files;
        if (!files || !files.length) {
            return;
        }
        const filelist: IFile[] = Array.from(files).map((v: File) => {
            (v as unknown as IFile).uniqueId = uniqueId('file_upload_');
            return v as IFile;
        });

        if (!validateExt(filelist)) {
            return extError(filelist);
        }
        if (validate && !validate(files)) {
            return;
        }
        if (onCheck) {
            const isOnCheck = await onCheck(filelist);
            if (!isOnCheck) {
                if (input && input.current) {
                    input.current.value = '';
                }
                return;
            }
        }
        if (onStart) {
            onStart(filelist);
        }

        const total = filelist.length;
        let residual = total;
        const previewList = filelist.map(preview);
        const results = previewList.map(encode);
        onSelectReady && onSelectReady(previewList);
        if (onlyPreview) {
            event.target.value = '';
            return;
        }
        const payloadDatas: IEndData[] = [];
        if (onData) {
            results.map((result) => {
                result
                    .then((result) =>
                        getUploadResult({
                            ...result,
                            supportsResumeUpload,
                        }),
                    )
                    .then((payloadData) => {
                        const index = total - residual;
                        residual--;
                        onData(
                            payloadData.previewUrl,
                            payloadData.url,
                            payloadData.fileName,
                            String(payloadData.resourceId),
                            filelist[index],
                        );
                        payloadDatas.push({
                            previewUrl: payloadData.previewUrl,
                            url: payloadData.url,
                            fileName: payloadData.fileName,
                            resourceId: String(payloadData.resourceId),
                        });
                        if (!residual && onEnd) {
                            onEnd(payloadDatas);
                        }
                    })
                    .catch((e) => {
                        if (onUploadError) {
                            onUploadError(e, filelist);
                        }
                    });
            });
        }

        event.target.value = '';
    }

    const validateAccept = () => {
        if (isValidate) {
            return '';
        }
        if (isHarmony) {
            return ''; // 鸿蒙下不设 accept，避免无法调起；格式由 validateExt 等校验
        }
        return ua.isWeixinBrowser && ua.isAndroid ? '' : inputAccept;
    };
    const clsNames = classSet('file-select', className || '');

    let isMultiple = multiple;
    const isHarmony = isHarmonyOS();
    if (isHarmony) {
        isMultiple = false;
    }

    const handleLabelClick = (e: React.MouseEvent<HTMLLabelElement>) => {
        if (!disabled && input.current) {
            e.preventDefault(); // 避免 label 的 htmlFor 与下方 input.click() 双重触发，导致弹两次文件选择框
            input.current.click();
        }
        propsOnClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
    };

    return (
        <div className={clsNames} {...props}>
            <label
                htmlFor={disabled ? undefined : inputId}
                className="file-select-label-harmony file-select-content-wrap"
                style={disabled ? { cursor: 'default' } : undefined}
                onClick={handleLabelClick}
            >
                {children}
            </label>
            <input
                id={inputId}
                title=""
                type="file"
                ref={input}
                className={[className ? `${className}-input` : '', 'file-select-input-harmony'].filter(Boolean).join(' ')}
                multiple={isMultiple}
                disabled={disabled}
                onChange={processFiles}
                accept={validateAccept()}
                aria-hidden
            />
        </div>
    );
}
